import {isFeatureEnabled} from 'feature-flags';
import {Icon, Show} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useGetAutotracerPlConfigQuery,
  useGetCarVerticalConfigQuery,
  useGetCebiaAutotracerAndReportConfigQuery,
  useGetCebiaFeatureDecoderConfigQuery,
  useGetCebiaRokvyConfigQuery,
  useGetCebiaVinDecoderConfigQuery,
  useGetDatVinDecoderConfigQuery,
  useGetOmneticVinDecoderConfigQuery,
  useGetTenantQuery,
  usePatchAutotracerPlConfigMutation,
  usePatchCarVerticalConfigMutation,
  usePatchCebiaAutotracerAndReportConfigMutation,
  usePatchCebiaFeatureDecoderConfigMutation,
  usePatchCebiaRokvyConfigMutation,
  usePatchCebiaVinDecoderConfigMutation,
  usePatchDatVinDecoderConfigMutation,
  usePatchOmneticVinDecoderConfigMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {StackItem, Wrapper} from '@dms/settings/shared';
import {handleApiError, useIsLicenceEnabled, usePermissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {VehicleServiceContentItem} from './VehicleServiceContentItem';

export function VehicleServicesList() {
  const [canUpdateAutotracerPlConfig, canUpdateDatVinDecoderConfig] = usePermissions({
    permissionKeys: ['updateAutotracerPlConfig', 'updateDatVinDecoderConfig'],
  });

  const {data: cebiaAutotracerAndReport} = useGetCebiaAutotracerAndReportConfigQuery();
  const {data: cebiaFeatureDecoder} = useGetCebiaFeatureDecoderConfigQuery();
  const {data: cebiaRokvy} = useGetCebiaRokvyConfigQuery();
  const {data: omneticVinDecoder} = useGetOmneticVinDecoderConfigQuery();
  const {data: cebiaVinDecoder} = useGetCebiaVinDecoderConfigQuery();
  const {data: datVinDecoder} = useGetDatVinDecoderConfigQuery(undefined, {
    skip: !canUpdateDatVinDecoderConfig,
  });
  const {data: carVertical} = useGetCarVerticalConfigQuery();
  const {data: autotracerPlConfig} = useGetAutotracerPlConfigQuery(undefined, {
    skip: !canUpdateAutotracerPlConfig,
  });
  const {data: tenantData} = useGetTenantQuery();
  const [patchCebiaAutotracerAndReportConfig] = usePatchCebiaAutotracerAndReportConfigMutation();
  const [patchCebiaFeatureDecoderConfig] = usePatchCebiaFeatureDecoderConfigMutation();
  const [patchCebiaRokvyConfig] = usePatchCebiaRokvyConfigMutation();
  const [patchOmneticVinDecoder] = usePatchOmneticVinDecoderConfigMutation();
  const [patchCebiaVinDecoder] = usePatchCebiaVinDecoderConfigMutation();
  const [patchDatVinDecoder] = usePatchDatVinDecoderConfigMutation();
  const [patchCarVerticalConfig] = usePatchCarVerticalConfigMutation();
  const [patchAutotracerPlConfig] = usePatchAutotracerPlConfigMutation();

  const isBelgianTenant = tenantData?.country === 'BEL';
  const {isCarPassEnabled} = useIsLicenceEnabled();

  const updateAutotracerAndReport = (enabled: boolean) => {
    patchCebiaAutotracerAndReportConfig({
      cebiaAutotracerAndReportConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCebiaVinDecoder = (enabled: boolean) => {
    patchCebiaVinDecoder({
      body: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCebiaFeatureDecoder = (enabled: boolean) => {
    patchCebiaFeatureDecoderConfig({
      cebiaFeatureDecoderConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCebiaRokvy = (enabled: boolean) => {
    patchCebiaRokvyConfig({
      cebiaRokvyConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateOmneticVinDecoder = (enabled: boolean) => {
    if (!isFeatureEnabled(featureFlags.OMNETIC_VIN_DECODER)) {
      throw new Error('Feature flag OMNETIC_VIN_DECODER is not enabled!');
    }

    patchOmneticVinDecoder({
      body: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  // The switch stays disabled until the dealer stores its DAT credentials, so the subtext
  // has to say what is missing — the row itself already links to the credentials form.
  const datVinDecoderLabel = isNil(datVinDecoder?.credentials)
    ? i18n.t('page.integrations.labels.datVinDecoderMissingCredentials')
    : i18n.t('page.integrations.labels.datVinDecoderDescription');

  const updateDatVinDecoder = (enabled: boolean) => {
    patchDatVinDecoder({
      body: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateCarVertical = (enabled: boolean) => {
    patchCarVerticalConfig({
      carVerticalConfigRequestBody: {enabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const updateAutotracerPl = (enabled: boolean) => {
    patchAutotracerPlConfig({enabled}).unwrap().catch(handleApiError);
  };

  const items = buildArray<{id: string; title: ReactNode; url?: string}>([
    {
      id: 'cebia-equipment-autotrace',
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-equipment-autotrace"
          isDisabled={!cebiaAutotracerAndReport?.credentials}
          switcherOn={cebiaAutotracerAndReport?.enabled}
          onSwitcherToggle={updateAutotracerAndReport}
          title={i18n.t('page.integrations.labels.cebia')}
          label={i18n.t('page.integrations.labels.cebiaAutotraceDescription')}
        />
      ),
      url: settingsRoutes.vehicleServicesCebia,
    },
    {
      id: 'cebia-vin-decoder',
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-vin-decoder"
          switcherOn={cebiaVinDecoder?.enabled}
          onSwitcherToggle={updateCebiaVinDecoder}
          title={i18n.t('page.integrations.labels.cebiaVinDecoder')}
          label={i18n.t('page.integrations.labels.cebiaVinDecoderSubtext')}
        />
      ),
    },
    {
      id: 'cebia-equipment-switcher',
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-equipment-switcher"
          isDisabled={!cebiaFeatureDecoder?.credentials}
          switcherOn={cebiaFeatureDecoder?.enabled}
          onSwitcherToggle={updateCebiaFeatureDecoder}
          title={i18n.t('page.integrations.labels.cebiaEquipment')}
          label={i18n.t('page.integrations.labels.cebiaEquipmentSubtext')}
        />
      ),
      url: settingsRoutes.vehicleServicesFeatures,
    },
    {
      id: 'cebia-rokvy-switcher',
      title: (
        <VehicleServiceContentItem
          switcherName="cebia-rokvy-switcher"
          isDisabled={!cebiaRokvy?.credentials}
          switcherOn={cebiaRokvy?.enabled}
          onSwitcherToggle={updateCebiaRokvy}
          title={i18n.t('page.integrations.labels.cebiaRokvy')}
          label={i18n.t('page.integrations.labels.cebiaRokvyDescription')}
        />
      ),
      url: settingsRoutes.vehicleServicesRokVy,
    },
  ])
    .when(canUpdateAutotracerPlConfig, {
      id: 'autotracer-pl',
      title: (
        <VehicleServiceContentItem
          switcherName="autotracer-pl"
          switcherOn={autotracerPlConfig?.enabled}
          onSwitcherToggle={updateAutotracerPl}
          title={i18n.t('page.integrations.labels.autotracerPlTitle')}
          label={i18n.t('page.integrations.labels.autotracerPlDescription')}
        />
      ),
    })
    .whenFeatureEnabled(featureFlags.CAR_VERTICAL_INTEGRATION, {
      id: 'car-vertical-switcher',
      title: (
        <VehicleServiceContentItem
          switcherName="car-vertical-switcher"
          switcherOn={carVertical?.enabled}
          onSwitcherToggle={updateCarVertical}
          title={i18n.t('page.integrations.labels.carVertical')}
          label={i18n.t('page.integrations.labels.carVerticalDescription')}
        />
      ),
    })
    .when(isFeatureEnabled(featureFlags.DAT_VIN_DECODER) && canUpdateDatVinDecoderConfig, {
      id: 'dat-vin-decoder',
      title: (
        <VehicleServiceContentItem
          switcherName="dat-vin-decoder"
          isDisabled={isNil(datVinDecoder?.credentials)}
          switcherOn={datVinDecoder?.enabled}
          onSwitcherToggle={updateDatVinDecoder}
          title={i18n.t('page.integrations.labels.datVinDecoder')}
          label={datVinDecoderLabel}
        />
      ),
      url: settingsRoutes.vehicleServicesDat,
    })
    .whenFeatureEnabled(featureFlags.OMNETIC_VIN_DECODER, {
      id: 'omnetic-vin-decoder',
      title: (
        <VehicleServiceContentItem
          switcherName="omnetic-vin-decoder"
          switcherOn={omneticVinDecoder?.enabled}
          onSwitcherToggle={updateOmneticVinDecoder}
          title={i18n.t('page.integrations.labels.omneticVinDecoder')}
          label={i18n.t('page.integrations.labels.omneticVinDecoderDescription')}
        />
      ),
    });

  return (
    <Wrapper>
      <Show when={isCarPassEnabled && isBelgianTenant}>
        <VehicleServicesStackItemInner
          key="carpass"
          to={settingsRoutes.vehicleServicesCarpass}
          data-testid={testIds.settings.vehicleServicesCarpass()}
        >
          {i18n.t('page.integrations.labels.carpassConfig')}
          <Icon value="navigation/chevron_right" />
        </VehicleServicesStackItemInner>
      </Show>
      {items.map(({id, title, url}, index) => (
        <VehicleServicesStackItemInner
          key={id}
          to={url}
          data-testid={testIds.settings.vehicleServices(`link-${index}`)}
        >
          {title}
          <Show when={isNotNil(url)}>
            <Icon value="navigation/chevron_right" />
          </Show>
        </VehicleServicesStackItemInner>
      ))}
    </Wrapper>
  );
}

const VehicleServicesStackItemInner = styled(StackItem)`
  align-items: start;
`;
